import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { 
  FiSave, FiMap, FiMapPin, FiTruck, FiDollarSign, FiPlus, FiTrash2, 
  FiEdit2, FiCheck, FiX, FiAlertCircle, FiInfo
} from 'react-icons/fi';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DeliveryConfigPage() {
  const [config, setConfig] = useState({
    depot_address: '',
    depot_city: '',
    depot_postal_code: '',
    free_radius_km: 25,
    default_km_rate: 1.5,
    max_distance_km: 300,
    km_tiers: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [newTier, setNewTier] = useState({ name: '', min_km: 0, max_km: null, price: 0 });
  const [showAddTier, setShowAddTier] = useState(false);

  // Test calculation
  const [testDistance, setTestDistance] = useState(50);
  const [testResult, setTestResult] = useState(null);
  const [testFormuleFreeKm, setTestFormuleFreeKm] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/geo/delivery-config`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement de la configuration');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/geo/delivery-config`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(config)
      });
      if (response.ok) {
        toast.success('Configuration enregistrée');
      } else {
        toast.error('Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const addTier = () => {
    if (!newTier.name) {
      toast.error('Veuillez donner un nom à la tranche');
      return;
    }
    setConfig(prev => ({
      ...prev,
      km_tiers: [...prev.km_tiers, { ...newTier }]
    }));
    setNewTier({ name: '', min_km: 0, max_km: null, price: 0 });
    setShowAddTier(false);
    toast.success('Tranche ajoutée');
  };

  const updateTier = (index, field, value) => {
    setConfig(prev => ({
      ...prev,
      km_tiers: prev.km_tiers.map((tier, i) => 
        i === index ? { ...tier, [field]: value } : tier
      )
    }));
  };

  const deleteTier = (index) => {
    setConfig(prev => ({
      ...prev,
      km_tiers: prev.km_tiers.filter((_, i) => i !== index)
    }));
    toast.success('Tranche supprimée');
  };

  const testCalculation = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/geo/calculate-km-fee?distance_km=${testDistance}&formula_has_free_km=${testFormuleFreeKm}`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }}
      );
      if (response.ok) {
        const data = await response.json();
        setTestResult(data);
      }
    } catch (error) {
      toast.error('Erreur lors du calcul');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Configuration des Frais Kilométriques</h1>
          <p className="text-gray-500">Gérez l'adresse de départ et les tarifs par tranche de distance</p>
        </div>
        <Button onClick={saveConfig} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
          <FiSave className="mr-2 h-4 w-4" />
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Depot Address */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FiMapPin className="h-5 w-5 text-amber-600" />
              Adresse de Départ (Dépôt)
            </CardTitle>
            <CardDescription>
              L'adresse depuis laquelle seront calculés les frais kilométriques
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Adresse complète</Label>
              <Input 
                value={config.depot_address || ''} 
                onChange={e => setConfig(prev => ({ ...prev, depot_address: e.target.value }))}
                placeholder="123 Rue Exemple"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Ville</Label>
                <Input 
                  value={config.depot_city || ''} 
                  onChange={e => setConfig(prev => ({ ...prev, depot_city: e.target.value }))}
                  placeholder="Istres"
                />
              </div>
              <div>
                <Label>Code Postal</Label>
                <Input 
                  value={config.depot_postal_code || ''} 
                  onChange={e => setConfig(prev => ({ ...prev, depot_postal_code: e.target.value }))}
                  placeholder="13800"
                />
              </div>
            </div>
            
            <Separator />
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Rayon de livraison gratuite (km)</Label>
                <Input 
                  type="number"
                  value={config.free_radius_km || 25} 
                  onChange={e => setConfig(prev => ({ ...prev, free_radius_km: parseFloat(e.target.value) }))}
                />
                <p className="text-xs text-gray-500 mt-1">Distance offerte pour toutes les formules</p>
              </div>
              <div>
                <Label>Tarif par km (hors tranches)</Label>
                <Input 
                  type="number"
                  step="0.10"
                  value={config.default_km_rate || 1.5} 
                  onChange={e => setConfig(prev => ({ ...prev, default_km_rate: parseFloat(e.target.value) }))}
                />
                <p className="text-xs text-gray-500 mt-1">Tarif si aucune tranche ne s'applique</p>
              </div>
            </div>
            
            <div>
              <Label>Distance maximum (km)</Label>
              <Input 
                type="number"
                value={config.max_distance_km || 300} 
                onChange={e => setConfig(prev => ({ ...prev, max_distance_km: parseFloat(e.target.value) }))}
              />
              <p className="text-xs text-gray-500 mt-1">Au-delà, livraison non disponible</p>
            </div>
          </CardContent>
        </Card>

        {/* Test Calculator */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FiTruck className="h-5 w-5 text-amber-600" />
              Simulateur de Tarif
            </CardTitle>
            <CardDescription>
              Testez le calcul des frais pour une distance donnée
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Distance en km</Label>
              <Input 
                type="number"
                value={testDistance} 
                onChange={e => setTestDistance(parseFloat(e.target.value))}
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input 
                type="checkbox"
                id="freeKm"
                checked={testFormuleFreeKm}
                onChange={e => setTestFormuleFreeKm(e.target.checked)}
                className="rounded border-gray-300"
              />
              <Label htmlFor="freeKm" className="cursor-pointer">
                Formule avec frais km OFFERTS
              </Label>
            </div>
            
            <Button onClick={testCalculation} className="w-full">
              Calculer
            </Button>
            
            {testResult && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 rounded-lg ${testResult.free_delivery ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600">Distance :</span>
                  <span className="font-semibold">{testResult.distance_km} km</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600">Tranche appliquée :</span>
                  <Badge variant={testResult.free_delivery ? 'success' : 'default'}>
                    {testResult.tier_applied}
                  </Badge>
                </div>
                <Separator className="my-2" />
                <div className="flex items-center justify-between">
                  <span className="font-medium">Frais de livraison :</span>
                  <span className={`text-2xl font-bold ${testResult.free_delivery ? 'text-green-600' : 'text-amber-600'}`}>
                    {testResult.delivery_fee === 0 ? 'OFFERT' : `${testResult.delivery_fee}€`}
                  </span>
                </div>
              </motion.div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* KM Tiers */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FiDollarSign className="h-5 w-5 text-amber-600" />
              Tranches de Tarification
            </CardTitle>
            <CardDescription>
              Définissez des forfaits par tranche de distance. Les formules "LUXE" et "VIP" peuvent offrir les frais km.
            </CardDescription>
          </div>
          <Button onClick={() => setShowAddTier(true)} variant="outline">
            <FiPlus className="mr-2 h-4 w-4" /> Ajouter une tranche
          </Button>
        </CardHeader>
        <CardContent>
          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <FiInfo className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Comment ça fonctionne ?</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>Les tranches sont appliquées dans l'ordre (de la plus petite à la plus grande distance)</li>
                <li>Si une formule inclut "Frais km OFFERTS", les frais seront automatiquement à 0€</li>
                <li>Le rayon de livraison gratuite s'applique à toutes les formules</li>
              </ul>
            </div>
          </div>

          {/* Add Tier Form */}
          <AnimatePresence>
            {showAddTier && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-6 p-4 bg-gray-50 rounded-lg border"
              >
                <h4 className="font-medium mb-4">Nouvelle tranche</h4>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label>Nom</Label>
                    <Input 
                      value={newTier.name}
                      onChange={e => setNewTier(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="ex: 26-50km"
                    />
                  </div>
                  <div>
                    <Label>Km minimum</Label>
                    <Input 
                      type="number"
                      value={newTier.min_km}
                      onChange={e => setNewTier(prev => ({ ...prev, min_km: parseFloat(e.target.value) }))}
                    />
                  </div>
                  <div>
                    <Label>Km maximum</Label>
                    <Input 
                      type="number"
                      value={newTier.max_km || ''}
                      onChange={e => setNewTier(prev => ({ ...prev, max_km: e.target.value ? parseFloat(e.target.value) : null }))}
                      placeholder="Vide = illimité"
                    />
                  </div>
                  <div>
                    <Label>Prix (€)</Label>
                    <Input 
                      type="number"
                      value={newTier.price}
                      onChange={e => setNewTier(prev => ({ ...prev, price: parseFloat(e.target.value) }))}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <Button variant="outline" onClick={() => setShowAddTier(false)}>
                    <FiX className="mr-2 h-4 w-4" /> Annuler
                  </Button>
                  <Button onClick={addTier} className="bg-amber-600 hover:bg-amber-700">
                    <FiCheck className="mr-2 h-4 w-4" /> Ajouter
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Tiers List */}
          <div className="space-y-3">
            {config.km_tiers?.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FiTruck className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>Aucune tranche configurée</p>
                <p className="text-sm">Le tarif par km sera utilisé par défaut</p>
              </div>
            ) : (
              config.km_tiers?.sort((a, b) => a.min_km - b.min_km).map((tier, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center gap-4 p-4 bg-white border rounded-lg hover:border-amber-200 transition-colors"
                >
                  <div className="flex-1 grid grid-cols-4 gap-4">
                    {editingTier === index ? (
                      <>
                        <Input 
                          value={tier.name}
                          onChange={e => updateTier(index, 'name', e.target.value)}
                        />
                        <Input 
                          type="number"
                          value={tier.min_km}
                          onChange={e => updateTier(index, 'min_km', parseFloat(e.target.value))}
                        />
                        <Input 
                          type="number"
                          value={tier.max_km || ''}
                          onChange={e => updateTier(index, 'max_km', e.target.value ? parseFloat(e.target.value) : null)}
                          placeholder="Illimité"
                        />
                        <Input 
                          type="number"
                          value={tier.price}
                          onChange={e => updateTier(index, 'price', parseFloat(e.target.value))}
                        />
                      </>
                    ) : (
                      <>
                        <div>
                          <span className="text-xs text-gray-500">Nom</span>
                          <p className="font-medium">{tier.name}</p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500">De</span>
                          <p className="font-medium">{tier.min_km} km</p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500">À</span>
                          <p className="font-medium">{tier.max_km ? `${tier.max_km} km` : 'Illimité'}</p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500">Prix</span>
                          <p className="font-bold text-amber-600">
                            {tier.price === 0 ? 'GRATUIT' : `${tier.price}€`}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {editingTier === index ? (
                      <Button size="sm" onClick={() => setEditingTier(null)}>
                        <FiCheck className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => setEditingTier(index)}>
                        <FiEdit2 className="h-4 w-4" />
                      </Button>
                    )}
                    <Button size="sm" variant="destructive" onClick={() => deleteTier(index)}>
                      <FiTrash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Help Section */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <FiAlertCircle className="h-6 w-6 text-amber-600 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-amber-900 mb-2">
                Comment intégrer les frais km dans les formules ?
              </h4>
              <ul className="text-sm text-amber-800 space-y-2">
                <li>• Les formules "LUXE", "VIP" ou "Premium" peuvent offrir les frais km en cochant l'option correspondante dans les paramètres du produit</li>
                <li>• Dans le récapitulatif de réservation, le système affichera automatiquement si les frais sont offerts ou facturés</li>
                <li>• Le calcul se fait automatiquement lors de la saisie du code postal du client</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
